import { router, setPageContent } from "..";
import { delFencealarm2, putFencealarm2 } from "../api/api";
import { PriceWrapper, prices } from "../billing/prices";
import {
	nameField,
	ownerField,
	sharingField,
	subscriptionField,
	tagsField,
} from "../device/device_settings_fields";
import { openUnregisterDeviceModal } from "../device/remove_device_modal";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { t } from "../locale";
import { removeNotificationListener } from "../mobile";
import { state } from "../state";
import { Bulma } from "../view/bulma";
import { centeredBox } from "../view/components";

/**
 * @type {import('navigo').Handler}
 */
async function fencealarm2SettingsPage(ctx) {
	if (!ctx?.data?.imei) throw "missing ctx.data.imei";
	const imei = ctx.data.imei;
	const fa2 = state.fencealarm2s[imei];

	const user = await state.getActiveUser();
	const isOwner = user.Email === fa2.Metadata.OwnerEmail;

	const {
		Fencealarm2Month: FenceAlarm2Month,
		Fencealarm2Year: FenceAlarm2Year,
	} = await prices;

	const voltageAlarm = voltageAlarmField(fa2);
	const subscription = subscriptionField(
		fa2.Subscription,
		[
			new PriceWrapper(user.Country),
			new PriceWrapper(user.Country, FenceAlarm2Month),
			new PriceWrapper(user.Country, FenceAlarm2Year),
		],
		isOwner,
	);
	const sharing = sharingField(fa2.Metadata);
	const tags = tagsField(fa2.Metadata);
	const name = nameField(fa2.Metadata.Name);
	const owner = ownerField(fa2.Metadata, isOwner);

	const onClickSave = () => {
		const attempt = errorSection.attempt(async () => {
			const fa2 = await putFencealarm2(imei, {
				VoltageAlarm: {
					Enabled: voltageAlarm.isEnabled(),
					Threshold: +voltageAlarm.getThreshold(),
				},
				Metadata: {
					Access: sharing.getAccess(),
					Notify: sharing.getNotify(),
					Tags: tags.getTags(),
					Name: name.getName(),
					Owner: owner.getOwner(),
				},
				Subscription: subscription.getPriceId(),
			});
			state.fencealarm2s[fa2.Imei] = fa2;
			router.jump(`/devices?id=${fa2.Imei}`);
		});
		loadingScreen.waitFor(attempt);
	};

	const onClickUnregister = () => {
		const attempt = errorSection.attempt(async () => {
			await delFencealarm2(imei);
			delete state.fencealarm2s[fa2.Imei];
			removeNotificationListener("fencealarm2", fa2.Imei);
			router.jump("/devices");
		});
		loadingScreen.waitFor(attempt);
	};

	setPageContent(
		centeredBox(
			"small",
			Bulma.title(`FenceAlarm2 - ${fa2.Metadata.Name}`, "is-4"),
			Bulma.subtitle(`IMEI: ${fa2.Imei}`, "is-6"),
			voltageAlarm.element,
			subscription.element,
			sharing.element,
			tags.element,
			name.element,
			owner.element,
			document.createElement("hr"),
			Bulma.buttons(
				Bulma.button(
					{
						onClick: onClickSave,
						styles: ["is-primary"],
					},
					t.save,
				),
				Bulma.button(
					{
						onClick: () => router.jump(`/devices?id=${fa2.Imei}`),
						styles: ["is-primary"],
					},
					t.back,
				),
				Bulma.button(
					{
						disabled: !isOwner,
						onClick: () =>
							openUnregisterDeviceModal(onClickUnregister),
						styles: ["is-danger"],
					},
					t.remove,
				),
			),
		),
	);
}

/**
 * @param {Fencealarm2} fa2
 */
function voltageAlarmField(fa2) {
	const isSubscribed =
		fa2.Subscription.PriceIds.Current !== "" ||
		Date.now() < new Date(fa2.Subscription.ExpiresAt).getTime();

	const onOffSelect = Bulma.select({
		disabled: !isSubscribed,
		options: [
			Bulma.option(t.on, "on", isSubscribed && fa2.VoltageAlarm.Enabled),
			Bulma.option(
				t.off,
				"off",
				!isSubscribed || !fa2.VoltageAlarm.Enabled,
			),
		],
	});

	let selectedAlarmLevel = fa2.Config_v0_3.AlarmLevel;
	if (fa2.PendingResponse_v0_3?.Config) {
		selectedAlarmLevel = fa2.PendingResponse_v0_3.Config.AlarmLevel;
	}

	const alarmLevelSelect = Bulma.select({
		options: Array.from({ length: 7 }, (_, i) => (i + 2) * 1000).map((i) =>
			Bulma.option(`${i} V`, i.toString(), i === selectedAlarmLevel),
		),
	});

	const element = Bulma.field(
		{},
		Bulma.label(t.fencealarm.settingsPage.voltageAlarmLabel),
		Bulma.field(
			{
				styles: ["is-grouped"],
			},
			Bulma.control({}, onOffSelect.element),
			Bulma.control({}, alarmLevelSelect.element),
		),
	);

	return {
		element,
		isEnabled: () => onOffSelect.getValue() === "on",
		getThreshold: () => alarmLevelSelect.getValue(),
	};
}

export { fencealarm2SettingsPage };
